import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import formOptions from "../../config/formOptions.json";
import { GraphQLClient } from "graphql-request";

import LabelSelector from "../../components/LabelSelector";
import DateInput from "../../components/DateInput";
import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton.jsx";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";

import { graphqlEndpoint } from "../../config";
import { parseUKDate } from "../../utils/dateUtiles";
import { CREATE_TASK, CREATE_TASK_VERSION } from "../../graph/tasks/mutations";
import { READ_TENANT_ROLES_BY_TENANT } from "../../graph/tenantRoles/queries";
import { READ_ALL_USERS } from "../../graph/users/queries";

const client = new GraphQLClient(graphqlEndpoint);

const allowedLabels =
  (formOptions?.labelOptions || []).map((l) => l.value) || [];

const taskSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.string().nullable(),
  status: yup.string().nullable(),
  labels: yup
    .array()
    .of(yup.string().oneOf(allowedLabels, "Invalid label"))
    .nullable(),
  accountableRoleId: yup.string().nullable(),
  assignedTenantUserId: yup.string().nullable(),
  dueDate: yup.string().nullable(),
  deferDate: yup.string().nullable(),
  cost: yup.string().nullable(),
  plannedDurationMin: yup
    .number()
    .typeError("Planned minutes must be a number")
    .integer("Use whole minutes")
    .min(0, "Must be ≥ 0")
    .nullable(),
  actualDurationMin: yup
    .number()
    .typeError("Actual minutes must be a number")
    .integer("Use whole minutes")
    .min(0, "Must be ≥ 0")
    .nullable(),
});

const CreateTaskForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();

  const [roles, setRoles] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);

  const passedProjectId = location.state?.projectId || null;
  const redirectPath = location.state?.redirectPath || "/manageTasks";

  const mode = location.state?.mode || "create";
  const taskId = location.state?.taskId || null;
  const changeType = location.state?.changeType || null;
  const passedInitialValues = location.state?.initialValues || null;

  const isVersionMode = mode === "version";

  const initialTaskValues = {
    title: passedInitialValues?.title || "",
    description: passedInitialValues?.description || "",
    status: passedInitialValues?.status || "INBOX",
    sequence: passedInitialValues?.sequence || "",
    labels: passedInitialValues?.labels || [],
    accountableRoleId: passedInitialValues?.accountableRoleId || "",
    assignedTenantUserId: passedInitialValues?.assignedTenantUserId || "",
    dueDate: passedInitialValues?.dueDate || "",
    deferDate: passedInitialValues?.deferDate || "",
    cost: passedInitialValues?.cost || "",
    plannedDurationMin: passedInitialValues?.plannedDurationMin || "",
    actualDurationMin: passedInitialValues?.actualDurationMin || "",
    projectId: passedProjectId || passedInitialValues?.projectId || "",
  };

  useEffect(() => {
    const fetchOptions = async () => {
      if (!tenantId) return;

      try {
        const [{ readTenantRolesByTenant }, { readAllUsers }] =
          await Promise.all([
            client.request(READ_TENANT_ROLES_BY_TENANT, { tenantId }),
            client.request(READ_ALL_USERS),
          ]);

        setRoles(readTenantRolesByTenant || []);

        const filteredUsers = (readAllUsers || []).filter(
          (user) => user.tenantID === tenantId && !user.archivedAt
        );

        setTenantUsers(filteredUsers);
      } catch (err) {
        console.error("Failed to load task options:", err);
      }
    };

    fetchOptions();
  }, [tenantId]);

  const numOrNull = (v) =>
    v === "" || v === null || v === undefined
      ? null
      : Number.isNaN(Number(v))
      ? null
      : parseInt(v, 10);

  const floatOrNull = (v) =>
    v === "" || v === null || v === undefined
      ? null
      : Number.isNaN(Number(v))
      ? null
      : parseFloat(v);

  const handleFormSubmit = async (values, { resetForm }) => {
    if (!tenantId) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Select a tenant before creating a task",
      });
      return;
    }

    if (isVersionMode && (!taskId || !changeType)) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Missing task version details",
      });
      return;
    }

    try {
      const cleanedValues = { ...values };

      for (const key of Object.keys(cleanedValues)) {
        if (cleanedValues[key] === "") cleanedValues[key] = null;
      }

      cleanedValues.tenantId = tenantId;
      cleanedValues.projectId = cleanedValues.projectId || null;
      cleanedValues.labels = cleanedValues.labels || [];
      cleanedValues.cost = floatOrNull(cleanedValues.cost);
      cleanedValues.plannedDurationMin = numOrNull(
        cleanedValues.plannedDurationMin
      );
      cleanedValues.actualDurationMin = numOrNull(
        cleanedValues.actualDurationMin
      );
      cleanedValues.dueDate = cleanedValues.dueDate
        ? parseUKDate(cleanedValues.dueDate)
        : null;
      cleanedValues.deferDate = cleanedValues.deferDate
        ? parseUKDate(cleanedValues.deferDate)
        : null;

      if (isVersionMode) {
        await client.request(CREATE_TASK_VERSION, {
          input: {
            taskId,
            changeType,
            title: cleanedValues.title,
            description: cleanedValues.description,
            labels: cleanedValues.labels,
            status: cleanedValues.status,
          },
        });
      } else {
        await client.request(CREATE_TASK, { input: cleanedValues });
      }

      setSnackbar({
        open: true,
        message: isVersionMode
          ? "Task version created successfully"
          : "Task created successfully",
        severity: "success",
      });

      resetForm();
      navigate(redirectPath, { state: { refresh: true } });
    } catch (err) {
      const message =
        err?.response?.errors?.[0]?.message || "Unexpected error";

      console.error(
        isVersionMode ? "Error creating task version:" : "Error creating task:",
        err
      );

      setSnackbar({
        open: true,
        message: isVersionMode
          ? `Failed to create task version: ${message}`
          : `Failed to create task: ${message}`,
        severity: "error",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title={isVersionMode ? "CREATE TASK VERSION" : "CREATE TASK"}
          subtitle="Select a tenant before creating a task"
        />
        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title={isVersionMode ? "CREATE TASK VERSION" : "CREATE TASK"}
        subtitle={
          isVersionMode
            ? `Create a ${changeType?.toLowerCase()} version of this SOP task`
            : passedProjectId
            ? "Create a task linked to this project"
            : `Create a standalone task for ${tenantName || "selected tenant"}`
        }
      />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialTaskValues}
        validationSchema={taskSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Title"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) =>
                  setFieldValue("description", nextValue)
                }
                label="Description"
                minRows={14}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              <LabelSelector
                selectedLabels={values.labels}
                setFieldValue={setFieldValue}
                error={errors.labels}
                touched={touched.labels}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Status"
                >
                  {(formOptions.taskStatusOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Sequence"
                name="sequence"
                value={values.sequence}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isVersionMode}
                sx={{ gridColumn: "span 2" }}
              />

              <input
                type="hidden"
                name="projectId"
                value={values.projectId || ""}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Accountable Role</InputLabel>
                <Select
                  name="accountableRoleId"
                  value={values.accountableRoleId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Accountable Role"
                  disabled={isVersionMode}
                >
                  <MenuItem value="">None</MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Assigned User</InputLabel>
                <Select
                  name="assignedTenantUserId"
                  value={values.assignedTenantUserId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Assigned User"
                  disabled={isVersionMode}
                >
                  <MenuItem value="">None</MenuItem>
                  {tenantUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {`${user.firstName || ""} ${
                        user.lastName || ""
                      }`.trim() || user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DateInput
                label="Due Date"
                name="dueDate"
                value={values.dueDate}
                onChange={setFieldValue}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                error={errors.dueDate}
                touched={touched.dueDate}
              />

              <DateInput
                label="Defer Until"
                name="deferDate"
                value={values.deferDate}
                onChange={setFieldValue}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                error={errors.deferDate}
                touched={touched.deferDate}
              />

              <TextField
                label="Planned minutes"
                name="plannedDurationMin"
                type="number"
                value={values.plannedDurationMin}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isVersionMode}
                inputProps={{ min: 0, step: 1 }}
                error={
                  !!touched.plannedDurationMin &&
                  !!errors.plannedDurationMin
                }
                helperText={
                  touched.plannedDurationMin && errors.plannedDurationMin
                }
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Actual minutes"
                name="actualDurationMin"
                type="number"
                value={values.actualDurationMin}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isVersionMode}
                inputProps={{ min: 0, step: 1 }}
                error={
                  !!touched.actualDurationMin &&
                  !!errors.actualDurationMin
                }
                helperText={
                  touched.actualDurationMin && errors.actualDurationMin
                }
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Cost"
                name="cost"
                type="number"
                value={values.cost}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isVersionMode}
                inputProps={{ min: 0, step: 0.01 }}
                error={!!touched.cost && !!errors.cost}
                helperText={touched.cost && errors.cost}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <ActionButton type="submit" tone="success">
                {isVersionMode
                  ? `Create ${
                      changeType === "MAJOR" ? "Major" : "Minor"
                    } Version`
                  : "Create Task"}
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateTaskForm;